/**
 * Location Mutations Hook
 * Handles location CRUD operations with proper error handling and cache invalidation
 */

import { useLogger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';
import type { CreateLocationData } from '../types';
import { locationNameTakenMessage } from '../utils/locationNameTaken';
import { shopifyLocationConnectedMessage } from '../utils/shopifyLocationConnected';

/**
 * The log line for a refusal the form shows under the Name or Shopify location
 * field, otherwise null. A toast and a Sentry event for an ordinary "pick
 * another" would only add noise.
 */
function fieldRefusal(error: unknown): string | null {
  if (locationNameTakenMessage(error)) return 'Location name already taken';
  if (shopifyLocationConnectedMessage(error)) return 'Shopify location connected to a partner warehouse';
  return null;
}

export function useLocationMutations() {
  const { t } = useTranslation('settings');
  const logger = useLogger();
  const utils = trpc.useUtils();

  const createLocation = trpc.locations.create.useMutation({
    onMutate: (locationData: CreateLocationData) => {
      logger.info('Creating location', {
        operation: 'create_location',
        locationName: locationData.name,
      });
    },
    onSuccess: (data) => {
      utils.locations.list.invalidate();

      logger.info('Location created successfully', {
        operation: 'create_location',
        locationId: data.id,
      });

      toast({
        title: 'Success',
        description: t('toast.locationCreated'),
      });
    },
    onError: (error) => {
      const refusal = fieldRefusal(error);
      if (refusal) {
        logger.info(refusal, { operation: 'create_location' });
        return;
      }

      Sentry.captureException(error, {
        tags: {
          feature: 'locations',
          operation: 'create_location',
        },
      });

      logger.error('Failed to create location', {
        operation: 'create_location',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedCreateLocation'),
        variant: 'destructive',
      });
    },
  });

  const updateLocation = trpc.locations.update.useMutation({
    onMutate: ({ locationId }) => {
      logger.info('Updating location', {
        operation: 'update_location',
        locationId,
      });
    },
    onSuccess: (data) => {
      utils.locations.list.invalidate();

      logger.info('Location updated successfully', {
        operation: 'update_location',
        locationId: data.id,
      });

      toast({
        title: 'Success',
        description: t('toast.locationUpdated'),
      });
    },
    onError: (error) => {
      const refusal = fieldRefusal(error);
      if (refusal) {
        logger.info(refusal, { operation: 'update_location' });
        return;
      }

      Sentry.captureException(error, {
        tags: {
          feature: 'locations',
          operation: 'update_location',
        },
      });

      logger.error('Failed to update location', {
        operation: 'update_location',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedUpdateLocation'),
        variant: 'destructive',
      });
    },
  });

  const deleteLocation = trpc.locations.deactivate.useMutation({
    onMutate: ({ locationId }) => {
      logger.info('Deleting location', {
        operation: 'delete_location',
        locationId,
      });
    },
    onSuccess: () => {
      utils.locations.list.invalidate();

      logger.info('Location deleted successfully', {
        operation: 'delete_location',
      });

      toast({
        title: 'Success',
        description: t('toast.locationDeactivated'),
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'locations',
          operation: 'delete_location',
        },
      });

      logger.error('Failed to delete location', {
        operation: 'delete_location',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedDeactivateLocation'),
        variant: 'destructive',
      });
    },
  });

  const reactivateLocation = trpc.locations.reactivate.useMutation({
    onMutate: ({ locationId }) => {
      logger.info('Reactivating location', {
        operation: 'reactivate_location',
        locationId,
      });
    },
    onSuccess: () => {
      utils.locations.list.invalidate();

      logger.info('Location reactivated successfully', {
        operation: 'reactivate_location',
      });

      toast({
        title: 'Success',
        description: t('toast.locationReactivated'),
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'locations',
          operation: 'reactivate_location',
        },
      });

      logger.error('Failed to reactivate location', {
        operation: 'reactivate_location',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedReactivateLocation'),
        variant: 'destructive',
      });
    },
  });

  return {
    createLocation,
    updateLocation,
    deleteLocation,
    reactivateLocation,
  };
}
